import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { gameService } from '../services/gameService';
import { ApiResponse, GameSession, Player, Question, Answer, AnswerResult, LeaderboardEntry, GameSpaceInfo } from '../types';
import signalRService from '../services/signalRService';
import * as signalR from '@microsoft/signalr';

// Define User type to include name property
interface User {
  id: string;
  name?: string;
  email?: string;
  [key: string]: any;
}

// Constants
const LAST_GAME_CODE_KEY = 'lastGameCode';
const LAST_GAME_TIMESTAMP_KEY = 'lastGameTimestamp';

// Define the ChatMessage interface for chat functionality
interface ChatMessage {
  userId: string;
  userName: string;
  message: string;
  timestamp: Date;
}

// Define the UserConnection interface for connection tracking
interface UserConnection {
  userId: string;
  userName: string;
  connectionId: string;
  isConnected: boolean;
  lastConnected: Date;
  lastDisconnected?: Date;
}

// Define the UserConnectionStatus interface for connection status updates
interface UserConnectionStatus {
  userId: string;
  userName?: string;
  isConnected: boolean;
  timestamp: Date;
}

interface GameState {
  gameSession: GameSession | null;
  currentGame: GameSession | null;
  isInGame: boolean;
  isLoading: boolean;
  error: string | null;
  publicGames: GameSession[];
  currentQuestion: Question | null;
  timeRemaining: number;
  isQuestionActive: boolean;
  leaderboard: LeaderboardEntry[];
  gameEnded: boolean;
  chatMessages: ChatMessage[];
  connectedUsers: UserConnection[];
  gameSpace: GameSpaceInfo | null;
}

interface GameContextType extends GameState {
  createGame: (gameData: {
    title: string;
    description?: string;
    isPublic: boolean;
    maxPlayers: number;
    questionCollectionId: string;
  }) => Promise<boolean>;
  joinGame: (gameCode: string, playerName?: string) => Promise<boolean>;
  joinGameSpace: (gameCode: string, playerName?: string) => Promise<boolean>;
  leaveGame: () => void;
  startGame: () => Promise<boolean>;
  startNextQuestion: () => Promise<boolean>;
  submitAnswer: (answer: Answer) => Promise<void>;
  getPublicGames: () => Promise<void>;
  resetError: () => void;
  clearError: () => void;
  refreshGameSession: () => Promise<boolean>;
  hasLastGame: () => boolean;
  rejoinLastGame: () => Promise<boolean>;
  getLastGameCode: () => string | null;
  sendChatMessage: (sessionId: number, message: string) => Promise<void>;
  isUserConnected: (userId: string) => boolean;
  getConnectedUsers: () => UserConnection[];
}

const initialState: GameState = {
  gameSession: null,
  currentGame: null,
  isInGame: false,
  isLoading: false,
  error: null,
  publicGames: [],
  currentQuestion: null,
  timeRemaining: 0,
  isQuestionActive: false,
  leaderboard: [],
  gameEnded: false,
  chatMessages: [],
  connectedUsers: [],
  gameSpace: null,
};

const GameContext = createContext<GameContextType | undefined>(undefined);

type GameAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PUBLIC_GAMES'; payload: GameSession[] }
  | { type: 'SET_GAME'; payload: GameSession | null }
  | { type: 'SET_GAME_SESSION'; payload: GameSession | null }
  | { type: 'JOIN_GAME'; payload: { session: GameSession } }
  | { type: 'JOIN_GAME_SPACE'; payload: { gameSpace: GameSpaceInfo } }
  | { type: 'LEAVE_GAME' }
  | { type: 'ADD_PLAYER'; payload: Player }
  | { type: 'REMOVE_PLAYER'; payload: string } // player id
  | { type: 'SET_CURRENT_QUESTION'; payload: Question | null }
  | { type: 'SET_TIME_REMAINING'; payload: number }
  | { type: 'SET_QUESTION_ACTIVE'; payload: boolean }
  | { type: 'UPDATE_LEADERBOARD'; payload: LeaderboardEntry[] }
  | { type: 'SET_GAME_ENDED'; payload: boolean }
  | { type: 'QUESTION_STARTED'; payload: { question: Question; timeRemaining: number } }
  | { type: 'ANSWER_SUBMITTED'; payload: { playerId: string; isCorrect: boolean } }
  | { type: 'QUESTION_ENDED'; payload: { leaderboard: LeaderboardEntry[] } }
  | { type: 'GAME_ENDED'; payload: { finalLeaderboard: LeaderboardEntry[] } }
  | { type: 'UPDATE_TIMER'; payload: number }
  | { type: 'ADD_CHAT_MESSAGE'; payload: ChatMessage }
  | { type: 'SET_CONNECTED_USERS'; payload: UserConnection[] }
  | { type: 'UPDATE_USER_CONNECTION'; payload: UserConnectionStatus };

function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_PUBLIC_GAMES':
      return { ...state, publicGames: action.payload };
    case 'SET_GAME':
      // Update gameSession.game if gameSession exists
      if (state.gameSession) {
        return { ...state, gameSession: { ...state.gameSession } };
      }
      // If no gameSession exists, create a minimal one with just the game
      return {
        ...state,
        gameSession: action.payload
          ? {
              id: 0,
              gameCode: '',
              status: 0,
              leaderboard: [],
              statusName: 'Created',
              gameType: '',
              createdAt: '',
              startTime: '',
              endTime: '',
              isPublic: false,
              isActive: false,
              totalQuestionCount: 0,
              currentQuestionIndex: 0,
              maxPlayers: 0,
              currentPlayerCount: 0,
              questionCollectionId: 0,
              questionCollectionTitle: '',
              allowExternalHost: false,
              gameSpaceId: 0,
              hostId: 0,
              hostName: '',
              hostUserId: '',
              title: '',
              players: [],
            }
          : null,
      };
    case 'SET_GAME_SESSION':
      return {
        ...state,
        gameSession: action.payload,
        isInGame: !!action.payload,
        currentGame: action.payload,
      };
    case 'JOIN_GAME':
      // Update all relevant state fields when joining a game
      return {
        ...state,
        gameSession: action.payload.session,
        currentGame: action.payload.session,
        isInGame: true,
        error: null,
      };
    case 'JOIN_GAME_SPACE':
      return {
        ...state,
        gameSpace: action.payload.gameSpace,
        isInGame: false,
        error: null,
      };
    case 'LEAVE_GAME':
      return {
        ...state,
        gameSession: null,
        isInGame: false,
        currentQuestion: null,
        timeRemaining: 0,
        isQuestionActive: false,
        leaderboard: [],
        gameEnded: false,
      };
    case 'ADD_PLAYER':
      if (!state.gameSession) return state;
      return {
        ...state,
        gameSession: {
          ...state.gameSession,
          players: [...state.gameSession.players, action.payload],
        },
      };
    case 'REMOVE_PLAYER':
      if (!state.gameSession) return state;
      return {
        ...state,
        gameSession: {
          ...state.gameSession,
          players: state.gameSession.players.filter((p) => p.id !== action.payload),
        },
      };
    case 'SET_CURRENT_QUESTION':
      return { ...state, currentQuestion: action.payload, isQuestionActive: action.payload !== null };
    case 'SET_TIME_REMAINING':
      return { ...state, timeRemaining: action.payload };
    case 'SET_QUESTION_ACTIVE':
      return { ...state, isQuestionActive: action.payload };
    case 'UPDATE_LEADERBOARD':
      return { ...state, leaderboard: action.payload };
    case 'SET_GAME_ENDED':
      return { ...state, gameEnded: action.payload };
    case 'QUESTION_STARTED':
      return {
        ...state,
        currentQuestion: action.payload.question,
        timeRemaining: action.payload.timeRemaining,
        isQuestionActive: true,
      };
    case 'ANSWER_SUBMITTED':
      return {
        ...state,
        leaderboard: state.leaderboard.map((entry) => {
          if (entry.playerId === action.payload.playerId) {
            return { ...entry, score: entry.score + (action.payload.isCorrect ? 1 : 0) };
          }
          return entry;
        }),
      };
    case 'QUESTION_ENDED':
      return {
        ...state,
        isQuestionActive: false,
        leaderboard: action.payload.leaderboard,
      };
    case 'GAME_ENDED':
      return {
        ...state,
        gameEnded: true,
        leaderboard: action.payload.finalLeaderboard,
      };
    case 'UPDATE_TIMER':
      return {
        ...state,
        timeRemaining: action.payload,
      };
    case 'ADD_CHAT_MESSAGE':
      return {
        ...state,
        chatMessages: [...state.chatMessages, action.payload],
      };
    case 'SET_CONNECTED_USERS':
      return {
        ...state,
        connectedUsers: action.payload,
      };
    case 'UPDATE_USER_CONNECTION':
      return {
        ...state,
        connectedUsers: state.connectedUsers.map(user => {
          if (user.userId === action.payload.userId) {
            return {
              ...user,
              isConnected: action.payload.isConnected,
              ...(action.payload.isConnected
                ? { lastConnected: action.payload.timestamp }
                : { lastDisconnected: action.payload.timestamp })
            };
          }
          return user;
        }),
      };
    default:
      return state;
  }
}

export function GameProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [state, dispatch] = useReducer(gameReducer, initialState);

  // Set up SignalR connection with all handlers centralized
  const setupSignalR = useCallback(async () => {
    try {
      await signalRService.connect();
      console.log('[GameContext] SignalR connected successfully');
      
      // === Core Connection Handlers ===
      signalRService.onError((errorMessage) => {
        console.log('[GameContext] SignalR error:', errorMessage);
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
      });
      
      signalRService.onConnectedUsers((users) => {
        console.log('[GameContext] Connected users received:', users);
        dispatch({ type: 'SET_CONNECTED_USERS', payload: users });
      });
      
      signalRService.onUserConnected((status) => {
        console.log('[GameContext] User connected:', status);
        dispatch({ type: 'UPDATE_USER_CONNECTION', payload: status });
      });
      
      signalRService.onUserDisconnected((status) => {
        console.log('[GameContext] User disconnected:', status);
        // Refresh game session to get updated player list
        refreshGameSession();
      });
      
      // === Chat Handlers ===
      signalRService.onChatMessage((message) => {
        console.log('[GameContext] Chat message received:', message);
        dispatch({ type: 'ADD_CHAT_MESSAGE', payload: message });
      });
      
      // === Game Flow Handlers ===
      signalRService.onGameStarted((data) => {
        console.log('[GameContext] Game started event:', data);
        dispatch({ type: 'SET_GAME_SESSION', payload: data.gameSession });
        // Navigation to game flow should be handled by components listening to state
      });
      
      signalRService.onNextQuestion((data) => {
        console.log('[GameContext] Next question event:', data);
        if (data && data.question) {
          dispatch({ type: 'SET_CURRENT_QUESTION', payload: data.question });
          dispatch({ type: 'SET_TIME_REMAINING', payload: data.timeRemaining || 30 });
          dispatch({ type: 'SET_QUESTION_ACTIVE', payload: true });
        }
      });
      
      signalRService.onQuestionEnded((data) => {
        console.log('[GameContext] Question ended event:', data);
        dispatch({ type: 'SET_TIME_REMAINING', payload: 0 });
        dispatch({ type: 'SET_QUESTION_ACTIVE', payload: false });
        if (data.leaderboard) {
          dispatch({ type: 'UPDATE_LEADERBOARD', payload: data.leaderboard });
        }
      });
      
      signalRService.onGameEnded((data) => {
        console.log('[GameContext] Game ended event:', data);
        dispatch({ type: 'SET_GAME_ENDED', payload: true });
        if (data.finalLeaderboard) {
          dispatch({ type: 'UPDATE_LEADERBOARD', payload: data.finalLeaderboard });
        }
      });
      
      // === Player Answer Handlers ===
      signalRService.onPlayerAnswered((data) => {
        console.log('[GameContext] Player answered:', data);
        // This can be used to update UI showing which players have answered
      });
      
      signalRService.onAnswerSubmitted((data) => {
        console.log('[GameContext] Answer submitted by player:', data);
        // This can be used to update UI showing which players have answered
      });
      
      // === Player Join/Leave Handlers ===
      signalRService.onPlayerJoined((data) => {
        console.log('[GameContext] Player joined event:', data);
        // Refresh game session to get updated player list
        refreshGameSession();
      });
      
      console.log('[GameContext] All SignalR handlers registered successfully');
    } catch (error) {
      console.error('[GameContext] Failed to connect to SignalR:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to connect to game server' });
    }
  }, []);
  
  // Clean up SignalR connection
  const cleanupSignalR = useCallback(async () => {
    try {
      // Check if the SignalR service is connected before attempting cleanup
      const connectionState = signalRService.connectionState;
      
      // If we can't get the state or it's already disconnected, no need to clean up
      if (!connectionState || connectionState === signalR.HubConnectionState.Disconnected) {
        console.log('SignalR is already disconnected, no cleanup needed');
        return;
      }
      
      // Only remove listeners and disconnect if we have an active connection
      console.log(`SignalR cleanup started (current state: ${connectionState})`);
      signalRService.removeAllListeners();
      await signalRService.disconnect();
      console.log('SignalR disconnected successfully');
    } catch (error) {
      console.error('Error disconnecting from SignalR:', error);
    }
  }, []);

  useEffect(() => {
    // Set up SignalR connection when authenticated
    if (isAuthenticated) {
      setupSignalR();
    }

    return () => {
      // Clean up SignalR connection on unmount
      cleanupSignalR().catch(console.error);
    };
  }, [isAuthenticated, setupSignalR, cleanupSignalR]);

  // Send a chat message via SignalR
  const sendChatMessage = useCallback(async (sessionId: number, message: string) => {
    try {
      if (!user || !user.id) {
        console.error('User is not authenticated');
        return;
      }
      
      // Get the user name from the user object, defaulting to 'Anonymous' if not available
      // Using type assertion since we know user.name might exist even if TypeScript doesn't recognize it
      const userName = (user as any).name || 'Anonymous';
      
      await signalRService.sendChatMessage(sessionId, message);
      console.log(`Sent chat message to game session ${sessionId} via SignalR`);
      dispatch({
        type: 'ADD_CHAT_MESSAGE',
        payload: {
          userId: user.id,
          userName,
          message,
          timestamp: new Date(),
        },
      });
    } catch (error) {
      console.error('Failed to send chat message via SignalR:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to send chat message' });
    }
  }, [user, dispatch]);

  // Join a game group via SignalR to track connections
  const joinGameGroup = useCallback(async (gameCode: string, displayName: string) => {
    try {
      debugger;
      await signalRService.joinGameGroup(gameCode, displayName);
      console.log(`Joined SignalR game group for session ${gameCode}`);
    } catch (error) {
      console.error('Failed to join SignalR game group:', error);
    }
  }, []);

  // Leave a game via SignalR
  const leaveGameGroup = useCallback(async (gameCode: string) => {
    try {
      await signalRService.leaveGameGroup(gameCode);
      console.log(`Left game group ${gameCode} via SignalR`);
    } catch (error) {
      console.error('Failed to leave SignalR game group:', error);
    }
  }, []);
  
  // Check if a user is connected
  const isUserConnected = useCallback((userId: string): boolean => {
    return state.connectedUsers.some(user => user.userId == userId);
  }, [state.connectedUsers]);
  
  // Get connected users
  const getConnectedUsers = useCallback((): UserConnection[] => {
    return state.connectedUsers;
  }, [state.connectedUsers]);
  
  // Modified joinGame to also join the SignalR game group
  const joinGame = useCallback(async (gameCode: string, playerName?: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await gameService.joinGame(gameCode, playerName);
     debugger;
      if (result.success && result.data) {
        // Access the session from JoinGameResponse
        const gameSession = result.data.session;
        
        // Log the data structure to help with debugging
        console.log('Join game response:', result.data);
        console.log('Game session:', gameSession);
        
        dispatch({ type: 'JOIN_GAME', payload: { session: gameSession } });
        
        // Join the SignalR game group to track connections 
        // Ensure we have a valid gameSessionId
        if (!gameSession || !gameSession.id) {
          console.error('Invalid game session or missing ID:', gameSession);
          throw new Error('Game session ID is missing');
        }
        
        // Convert id to number if it's a string
        const gameSessionId = typeof gameSession.id === 'string' ? parseInt(gameSession.id, 10) : gameSession.id;
        console.log('Joining game group with session ID:', gameSessionId);
        const displayName = playerName || '';
        await joinGameGroup(gameCode, displayName);
        
        // Store last game code for potential rejoin
        localStorage.setItem(LAST_GAME_CODE_KEY, gameCode);
        localStorage.setItem(LAST_GAME_TIMESTAMP_KEY, Date.now().toString());
        
        return true;
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.message || 'Failed to join game' });
        return false;
      }
    } catch (error) {
      console.error('Error joining game:', error);
      dispatch({ type: 'SET_ERROR', payload: 'An error occurred while joining the game' });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [joinGameGroup]);
  
  // Join a GameSpace without requiring an active game session
  const joinGameSpace = useCallback(async (gameCode: string, playerName?: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await gameService.joinGameSpace(gameCode, playerName);
      if (result.success && result.data) {
        const gameSpace = result.data;
        
        // Log the data structure to help with debugging
        console.log('Join GameSpace response:', result.data);
        
        dispatch({ type: 'JOIN_GAME_SPACE', payload: { gameSpace } });
        
        // Join the SignalR GameSpace group to track connections
        console.log('Joining GameSpace SignalR group:', gameSpace.id);
        const displayName = playerName || '';
        // Create a group for the GameSpace
        await signalRService.joinGameSpaceGroup(`gamespace_${gameSpace.id}`, displayName);
        
        // Store last game code for potential rejoin
        localStorage.setItem(LAST_GAME_CODE_KEY, gameCode);
        localStorage.setItem(LAST_GAME_TIMESTAMP_KEY, Date.now().toString());
        
        return true;
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.message || 'Failed to join GameSpace' });
        return false;
      }
    } catch (error) {
      console.error('Error joining GameSpace:', error);
      dispatch({ type: 'SET_ERROR', payload: 'An error occurred while joining the GameSpace' });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);
  
  // Modified leaveGame to also leave the SignalR game group and update connection status in DB
  const leaveGame = useCallback(() => {
    debugger;
    if (state.gameSession) {
      const gameSessionId = state.gameSession.id;
      const gameCode = state.gameSession.gameCode;
      
      // Update the player's connection status in the database to false
      if (user && user.id) {
        console.log(`[GameContext] Updating connection status to false for user ${user.id} in session ${gameSessionId}`);
        gameService.updatePlayerConnectionStatus(gameSessionId, user.id, false)
          .then(response => {
            if (response.success) {
              console.log(`[GameContext] Successfully updated connection status in database`);
            } else {
              console.error(`[GameContext] Failed to update connection status:`, response.message);
            }
          })
          .catch(error => {
            console.error(`[GameContext] Error updating connection status:`, error);
          });
      }

      // Leave the SignalR game group
      leaveGameGroup(gameCode).catch(console.error);
      
      // Leave the game via API
      gameService.leaveGame(gameCode).catch(console.error);
      
      dispatch({ type: 'LEAVE_GAME' });
    }
  }, [state.gameSession, leaveGameGroup, user]);

  // Create a game via API
  const createGame = useCallback(async (gameData: {
    title: string;
    description?: string;
    isPublic: boolean;
    maxPlayers: number;
    questionCollectionId: string;
  }) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const result = await gameService.createGame(gameData);
      if (result.success && result.data) {
        dispatch({ type: 'SET_GAME_SESSION', payload: result.data });
        return true;
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.message || 'Failed to create game' });
        return false;
      }
    } catch (error) {
      console.error('Error creating game:', error);
      dispatch({ type: 'SET_ERROR', payload: 'An error occurred while creating the game' });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);
  
  // Submit an answer via API
  const submitAnswer = useCallback(async (answer: Answer) => {
    try {
      if (!state.gameSession) return;
      
      // The gameService.submitAnswer expects an Answer object directly
      // Make sure the answer has the gameSessionId property set
      const answerWithSessionId = {
        ...answer,
        gameSessionId: state.gameSession.id
      };
      
      await gameService.submitAnswer(answerWithSessionId);
    } catch (error) {
      console.error('Error submitting answer:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to submit answer' });
    }
  }, [state.gameSession, dispatch]);
  
  // Helper function to refresh game session
  const refreshGameSession = useCallback(async () => {
    if (!state.gameSession) return false;
    try {
      const sessionId = state.gameSession.id;
      const result = await gameService.getGameSession(sessionId.toString());
      if (result.success && result.data) {
        dispatch({ type: 'SET_GAME_SESSION', payload: result.data });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error refreshing game session:', error);
      return false;
    }
  }, [state.gameSession, dispatch]);

  // Start game via API
  const startGame = useCallback(async () => {
    if (!state.gameSession) return false;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const sessionId = state.gameSession.id;

      const result = await gameService.startGame(sessionId.toString());
      if (result.success) {
        await refreshGameSession();
        return true;
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.message || 'Failed to start game' });
        return false;
      }
    } catch (error) {
      console.error('Error starting game:', error);
      dispatch({ type: 'SET_ERROR', payload: 'An error occurred while starting the game' });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.gameSession, refreshGameSession, dispatch]);
  
  // Start next question via API
  const startNextQuestion = useCallback(async () => {
    if (!state.gameSession) return false;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      // Ensure we're passing the correct parameter type
      const sessionId = state.gameSession.id;
      
      // startNextQuestion expects a number parameter
      // Convert to number if it's a string
      const numericSessionId = typeof sessionId === 'string' ? parseInt(sessionId, 10) : sessionId;
      
      const result = await gameService.startNextQuestion(numericSessionId);
      if (result.success) {
        return true;
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.message || 'Failed to start next question' });
        return false;
      }
    } catch (error) {
      console.error('Error starting next question:', error);
      dispatch({ type: 'SET_ERROR', payload: 'An error occurred while starting the next question' });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.gameSession, dispatch]);

  const contextValue: GameContextType = {
    ...state,
    isInGame: !!state.gameSession,
    getPublicGames: async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const result = await gameService.getPublicGames();
        if (result.success && result.data) {
          dispatch({ type: 'SET_PUBLIC_GAMES', payload: result.data });
        }
      } catch (error) {
        console.error('Error fetching public games:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch public games' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    resetError: () => dispatch({ type: 'SET_ERROR', payload: null }),
    clearError: () => dispatch({ type: 'SET_ERROR', payload: null }),
    refreshGameSession,
    hasLastGame: () => {
      const lastGameCode = localStorage.getItem(LAST_GAME_CODE_KEY);
      const lastGameTimestamp = localStorage.getItem(LAST_GAME_TIMESTAMP_KEY);
      if (!lastGameCode || !lastGameTimestamp) return false;
      
      // Check if the last game was within the last 24 hours
      const now = Date.now();
      const lastTime = parseInt(lastGameTimestamp, 10);
      const hoursDiff = (now - lastTime) / (1000 * 60 * 60);
      return hoursDiff < 24;
    },
    rejoinLastGame: async () => {
      const lastGameCode = localStorage.getItem(LAST_GAME_CODE_KEY);
      if (!lastGameCode) return false;
      return joinGame(lastGameCode);
    },
    getLastGameCode: () => localStorage.getItem(LAST_GAME_CODE_KEY),
    createGame,
    joinGame,
    joinGameSpace,
    leaveGame,
    submitAnswer,
    startGame,
    startNextQuestion,
    sendChatMessage,
    isUserConnected,
    getConnectedUsers,
  };

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export function useGame() {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}
